import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../home/components/Footer';
import LaunchBanner from '../../components/feature/LaunchBanner';
import BookingCalendar from '../../components/feature/BookingCalendar';
import FacilityModal from './components/FacilityModal';
import MyBookings from './components/MyBookings';
import SeoHead from '../../components/SeoHead';

export default function Services() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFacilityModal, setShowFacilityModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigateToHome = (hash: string) => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const services = [
    {
      icon: 'ri-wheelchair-line',
      title: 'Wheelchair Transportation',
      description: 'Professional wheelchair transportation services designed for safety, comfort, and dignity. Our vehicles are equipped with secure wheelchair lifts and tie-down systems that meet all safety standards. Our drivers are trained in proper wheelchair handling, transfer assistance, and patient care protocols.',
      features: [
        'ADA-compliant vehicles with wheelchair lifts',
        'Secure tie-down systems for maximum safety',
        'Trained drivers in wheelchair handling',
        'Door-to-door assistance included',
        'Climate-controlled vehicles',
        'Real-time tracking and communication'
      ],
      whoFor: 'Assisted living facilities, rehabilitation centers, dialysis clinics, and private patients requiring wheelchair accessibility',
      pricing: null,
      badge: null,
    },
    {
      icon: 'ri-walk-line',
      title: 'Ambulatory Transportation',
      description: 'Comfortable and reliable transportation for patients who can walk independently or with minimal assistance. Our ambulatory services provide safe, timely rides to medical appointments with professional drivers who understand the unique needs of senior patients and those with mobility challenges.',
      features: [
        'Comfortable sedan or van transportation',
        'Assistance getting in and out of vehicle',
        'Door-to-door service',
        'Punctual pickup and drop-off',
        'Professional, courteous drivers',
        'Flexible scheduling options'
      ],
      whoFor: 'Assisted living residents, senior patients, rehabilitation patients, and anyone needing reliable medical appointment transportation',
      pricing: null,
      badge: null,
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Dialysis Transportation',
      description: 'Specialized transportation services for dialysis patients requiring multiple weekly treatments. We understand the critical importance of on-time arrival for dialysis appointments and the unique needs of dialysis patients. Our drivers are trained to provide compassionate care and ensure comfortable, stress-free transportation.',
      features: [
        'Recurring appointment scheduling',
        'Priority on-time guarantee',
        'Understanding of dialysis patient needs',
        'Comfortable positioning for post-treatment',
        'Direct facility coordination',
        'Consistent driver assignments when possible'
      ],
      whoFor: 'Dialysis clinics, assisted living facilities with dialysis patients, and private dialysis patients needing reliable recurring transportation',
      pricing: null,
      badge: null,
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Recurring Medical Appointments',
      description: 'Streamlined transportation solutions for patients with regular medical appointments. We offer scheduled recurring rides that eliminate the stress of arranging transportation for each appointment. Our system ensures consistency, reliability, and peace of mind for both patients and facility administrators.',
      features: [
        'Pre-scheduled recurring rides',
        'Automatic appointment reminders',
        'Consistent service and drivers',
        'Flexible schedule modifications',
        'Bulk scheduling discounts',
        'Dedicated facility coordination'
      ],
      whoFor: 'Assisted living communities, rehabilitation centers, patients with chronic conditions requiring regular medical visits',
      pricing: null,
      badge: null,
    },
    {
      icon: 'ri-hospital-line',
      title: 'Hospital Discharges',
      description: 'Safe and comfortable transportation from hospital to home or assisted living facility following discharge. We coordinate directly with hospital discharge planners to ensure smooth transitions. Our drivers provide extra care and attention during this vulnerable time, ensuring patients arrive safely at their destination.',
      features: [
        'Direct hospital coordination',
        'Flexible scheduling for discharge times',
        'Extra assistance and patient care',
        'Wheelchair and ambulatory options',
        'Communication with receiving facility',
        'Medical equipment accommodation'
      ],
      whoFor: 'Hospitals, assisted living facilities, rehabilitation centers, and families coordinating post-hospital care',
      pricing: null,
      badge: null,
    },
    {
      icon: 'ri-door-open-line',
      title: 'Door-to-Door Assistance',
      description: 'Comprehensive assistance from pickup location to final destination. Our door-to-door service means we don\'t just drop patients at the curb – we provide hands-on assistance getting in and out of vehicles, navigating facilities, and ensuring safe arrival at appointments. This extra level of care sets us apart.',
      features: [
        'Assistance from door to vehicle',
        'Help navigating medical facilities',
        'Wait time during appointments (when scheduled)',
        'Return assistance to residence',
        'Communication with facility staff',
        'Extra time for patient comfort and safety'
      ],
      whoFor: 'Assisted living residents, seniors with mobility challenges, patients requiring extra assistance and supervision',
      pricing: null,
      badge: null,
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Gurney / Stretcher Transport',
      description: 'Specialized stretcher transport for patients who must remain lying flat during their entire trip. Whether due to a recent surgery, acute injury, severe mobility impairment, or post-operative recovery, some patients simply cannot sit upright — and standard wheelchair vans aren\'t built for them. Our fully equipped stretcher van and dedicated two-person crew provide safe, dignified transport with clinical-level care from origin to destination.',
      features: [
        'Dedicated stretcher van — fully equipped',
        'Two-person certified crew on every transport',
        'Safe transfer and loading protocols',
        'Secure patient restraint and positioning',
        'Climate-controlled, comfortable ride',
        'Coordination with hospitals, SNFs, and care facilities',
        'Post-surgical and bariatric transport capable',
        'Door-to-door service — never curbside drop-off'
      ],
      whoFor: 'Post-surgical patients, individuals with severe mobility limitations, bariatric patients, hospital discharges requiring flat transport, and any patient whose condition prevents upright seating',
      pricing: {
        base: '$275',
        perMile: '$3.50 / mile',
        note: 'Two-person crew included in every trip'
      },
      badge: 'Specialized Service',
    }
  ];

  const seoJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${import.meta.env.VITE_SITE_URL}/services#webpage`,
        "url": `${import.meta.env.VITE_SITE_URL}/services`,
        "name": "NEMT Services - Wheelchair, Dialysis & Medical Transportation | Guardian Veterans LLC",
        "description": "Explore Guardian Veterans LLC's full range of Non-Emergency Medical Transportation services in West Valley Arizona: wheelchair transport, ambulatory rides, dialysis transport, hospital discharges, recurring appointments, and door-to-door assistance.",
        "inLanguage": "en-US",
        "isPartOf": {
          "@type": "WebSite",
          "@id": `${import.meta.env.VITE_SITE_URL}/#website`,
          "url": import.meta.env.VITE_SITE_URL,
          "name": "Guardian Veterans LLC"
        }
      },
      {
        "@type": "ItemList",
        "@id": `${import.meta.env.VITE_SITE_URL}/services#servicelist`,
        "name": "Guardian Veterans LLC NEMT Services",
        "description": "Comprehensive Non-Emergency Medical Transportation services in West Valley Arizona",
        "itemListElement": services.map((service, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Service",
            "@id": `${import.meta.env.VITE_SITE_URL}/services#service-${index + 1}`,
            "name": service.title,
            "description": service.description,
            "serviceType": service.title,
            "areaServed": {
              "@type": "Place",
              "name": "West Valley Arizona"
            },
            "provider": {
              "@type": "MedicalBusiness",
              "name": "Guardian Veterans LLC",
              "url": import.meta.env.VITE_SITE_URL,
              "telephone": "+16022956626",
              "email": "info@gvnemt.com",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "9117 N 181st Ave",
                "addressLocality": "Waddell",
                "addressRegion": "AZ",
                "postalCode": "85355",
                "addressCountry": "US"
              }
            }
          }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="NEMT Services - Wheelchair, Dialysis & Medical Transportation | Guardian Veterans LLC"
        description="Explore Guardian Veterans LLC's full range of Non-Emergency Medical Transportation services in West Valley Arizona: wheelchair transport, ambulatory rides, dialysis transport, hospital discharges, recurring appointments, and door-to-door assistance."
        canonical={`${import.meta.env.VITE_SITE_URL}/services`}
        jsonLd={seoJsonLd}
      />

      {showFacilityModal && <FacilityModal onClose={() => setShowFacilityModal(false)} />}

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/870e0649015e5e428345464cd8040611/5ff22250b3810874d729e8053808c4b2.png"
              alt="Guardian Veterans LLC"
              className="h-12 w-auto object-contain"
            />
          </button>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => navigate('/')} className="text-sm font-medium text-gray-300 hover:text-yellow-400 transition-colors whitespace-nowrap cursor-pointer">
              Home
            </button>
            <button onClick={() => setShowFacilityModal(true)} className="text-sm font-medium text-yellow-400 transition-colors whitespace-nowrap cursor-pointer">
              Partner with us
            </button>
            <button onClick={() => handleNavigateToHome('facility-form')} className="text-sm font-medium text-gray-300 hover:text-yellow-400 transition-colors whitespace-nowrap cursor-pointer">
              For Facilities
            </button>
            <button onClick={() => handleNavigateToHome('private-pay-form')} className="text-sm font-medium text-gray-300 hover:text-yellow-400 transition-colors whitespace-nowrap cursor-pointer">
              For Patients
            </button>
          </div>

          {/* Mobile: call + hamburger */}
          <div className="flex md:hidden items-center gap-3">
            <a href="tel:+16022956626" className="flex items-center gap-1.5 bg-yellow-500 text-black text-sm font-bold px-3 py-1.5 rounded-full whitespace-nowrap cursor-pointer">
              <i className="ri-phone-fill text-xs"></i>
              Call Us
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-9 h-9 flex items-center justify-center text-white cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black border-t border-white/10">
            <div className="px-6 py-4 flex flex-col gap-1">
              <button
                onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                className="text-left text-sm font-medium text-gray-300 hover:text-yellow-400 py-3 border-b border-white/10 transition-colors cursor-pointer"
              >
                Home
              </button>
              <button
                onClick={() => { setShowFacilityModal(true); setMobileMenuOpen(false); }}
                className="text-left text-sm font-medium text-yellow-400 py-3 border-b border-white/10 cursor-pointer"
              >
                Partner with us
              </button>
              <button
                onClick={() => { handleNavigateToHome('facility-form'); setMobileMenuOpen(false); }}
                className="text-left text-sm font-medium text-gray-300 hover:text-yellow-400 py-3 border-b border-white/10 transition-colors cursor-pointer"
              >
                For Facilities
              </button>
              <button
                onClick={() => { handleNavigateToHome('private-pay-form'); setMobileMenuOpen(false); }}
                className="text-left text-sm font-medium text-gray-300 hover:text-yellow-400 py-3 transition-colors cursor-pointer"
              >
                For Patients
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 via-black to-slate-900 pt-32 pb-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(234,179,8,0.1),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(234,179,8,0.1),transparent_50%)]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-yellow-500/20 backdrop-blur-sm border border-yellow-500/40 rounded-full px-4 py-2 mb-4">
            <i className="ri-rocket-line text-yellow-400 text-sm"></i>
            <span className="text-yellow-300 text-sm font-medium whitespace-nowrap">Launching Soon — West Valley, AZ</span>
          </div>

          <h2 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
            Our Transportation Services
          </h2>
          
          <p className="text-base sm:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed mb-6">
            Guardian Veterans is preparing to launch comprehensive Non-Emergency Medical Transportation services for assisted living communities, healthcare facilities, and private patients throughout the West Valley.
          </p>

          <div className="inline-flex items-start sm:items-center gap-2 bg-white/5 border border-white/10 rounded-2xl sm:rounded-full px-4 sm:px-5 py-3 sm:py-2 max-w-sm sm:max-w-none text-left sm:text-center">
            <i className="ri-information-line text-white/40 text-sm flex-shrink-0 mt-0.5 sm:mt-0"></i>
            <span className="text-white/50 text-xs sm:text-sm">We are currently in our pre-launch phase — gathering facility partners and early access signups</span>
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="space-y-12 sm:space-y-16">
            {services.map((service, index) => (
              <div 
                key={index}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-6 sm:gap-12 items-center`}
              >
                {/* Icon Panel */}
                <div className="w-full lg:w-1/2">
                  <div className={`relative rounded-2xl overflow-hidden ${service.badge ? 'bg-gradient-to-br from-slate-900 via-black to-yellow-950' : 'bg-gradient-to-br from-slate-900 via-black to-slate-800'} h-[180px] sm:h-[280px] lg:h-[400px] flex flex-col items-center justify-center gap-4 sm:gap-6`}>
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(234,179,8,0.08),transparent_70%)]"></div>
                    {service.badge && (
                      <div className="absolute top-4 right-4 bg-yellow-500 text-black text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap z-10">
                        {service.badge}
                      </div>
                    )}
                    <div className={`relative w-16 h-16 sm:w-24 sm:h-24 lg:w-28 lg:h-28 ${service.badge ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' : 'bg-gradient-to-br from-yellow-500 to-yellow-600'} rounded-2xl flex items-center justify-center`}>
                      <i className={`${service.icon} text-black text-4xl sm:text-5xl lg:text-6xl`}></i>
                    </div>
                    <div className="relative text-center px-6 sm:px-8">
                      <p className="text-yellow-400 font-bold text-sm sm:text-base lg:text-lg tracking-widest uppercase leading-tight">Guardian Veterans LLC</p>
                      <p className="text-white/50 text-xs sm:text-sm mt-1">Professional NEMT Services</p>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent"></div>
                  </div>
                </div>

                {/* Content */}
                <div className="w-full lg:w-1/2">
                  <div className="flex items-center gap-3 mb-3 sm:mb-4">
                    <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">
                      {service.title}
                    </h3>
                    {service.badge && (
                      <span className="hidden sm:inline-flex items-center gap-1 bg-yellow-100 text-yellow-700 text-xs font-bold px-2.5 py-1 rounded-full border border-yellow-300 whitespace-nowrap">
                        <i className="ri-star-fill text-yellow-500 text-xs"></i>
                        {service.badge}
                      </span>
                    )}
                  </div>
                  
                  <p className="text-base sm:text-lg text-slate-600 mb-5 sm:mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Pricing callout — only for Gurney */}
                  {service.pricing && (
                    <div className="mb-5 sm:mb-6 bg-gradient-to-br from-slate-900 to-black rounded-xl p-5 border border-yellow-500/40">
                      <div className="flex items-center gap-2 mb-3">
                        <i className="ri-price-tag-3-line text-yellow-400 text-base"></i>
                        <span className="text-xs font-bold text-yellow-400 uppercase tracking-wide">Service Rate</span>
                      </div>
                      <div className="flex items-end gap-3 mb-2">
                        <span className="text-4xl font-extrabold text-yellow-400">{service.pricing.base}</span>
                        <span className="text-white/60 text-sm mb-1">base rate</span>
                      </div>
                      <p className="text-yellow-500 font-semibold text-sm mb-2">+ {service.pricing.perMile}</p>
                      <p className="text-white/50 text-xs">{service.pricing.note}</p>
                    </div>
                  )}

                  {/* Features */}
                  <div className="mb-5 sm:mb-6">
                    <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3">
                      Key Features
                    </h4>
                    <div className="grid grid-cols-1 gap-2 sm:gap-3">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-yellow-500 text-lg"></i>
                          </div>
                          <span className="text-sm text-slate-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Who This Is For */}
                  <div className="bg-gradient-to-br from-slate-900 to-black rounded-xl p-4 sm:p-6 border border-yellow-500/20">
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-heart-line text-yellow-400 text-lg sm:text-xl"></i>
                      </div>
                      <div>
                        <h5 className="text-xs sm:text-sm font-bold text-yellow-400 uppercase tracking-wide mb-2">
                          Who This Service Is For
                        </h5>
                        <p className="text-xs sm:text-sm text-white/80 leading-relaxed">
                          {service.whoFor}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* CTA for Gurney */}
                  {service.badge && (
                    <button
                      onClick={() => handleNavigateToHome('private-pay-form')}
                      className="mt-5 w-full py-3.5 bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-sm rounded-xl transition-all cursor-pointer whitespace-nowrap"
                    >
                      Request Stretcher Transport
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Banner */}
      <div id="facility-form-section" className="bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-yellow-500/20 backdrop-blur-sm border border-yellow-500/40 rounded-full px-4 py-2 mb-6">
              <i className="ri-star-fill text-yellow-500 text-sm"></i>
              <span className="text-yellow-600 text-sm font-medium whitespace-nowrap">Launching Soon</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 sm:mb-6">
              Ready to Partner With Us?
            </h2>
            <p className="text-base sm:text-xl text-slate-600 mb-2 leading-relaxed max-w-2xl mx-auto">
              Choose the option that best fits your situation and join our early access list — or connect your facility for priority scheduling.
            </p>
          </div>

          {/* 3-card transport type selector */}
          <div className="grid sm:grid-cols-3 gap-5 mb-8">

            {/* Card 1 — Ambulatory & Wheelchair */}
            <div className="bg-white rounded-2xl border-2 border-slate-200 hover:border-yellow-400 transition-all p-7 flex flex-col">
              <div className="w-12 h-12 flex items-center justify-center bg-yellow-50 border border-yellow-200 rounded-xl mb-5">
                <i className="ri-walk-line text-yellow-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">Ambulatory &amp; Wheelchair</h3>
              <p className="text-slate-500 text-sm leading-relaxed mb-4 flex-1">
                For patients who walk independently or use a wheelchair. Rides to medical appointments, dialysis, and more.
              </p>
              <div className="bg-slate-50 rounded-xl px-4 py-3 mb-5">
                <p className="text-xs text-slate-500 mb-0.5">Starting from</p>
                <p className="text-xl font-extrabold text-slate-900">$65 – $75 <span className="text-sm font-normal text-slate-500">base rate</span></p>
                <p className="text-xs text-yellow-600 font-semibold mt-0.5">+ $3.50 / mile</p>
              </div>
              <button
                onClick={() => handleNavigateToHome('private-pay-form')}
                className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-sm rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                Join Early Access List
              </button>
            </div>

            {/* Card 2 — Gurney / Stretcher (highlighted) */}
            <div className="bg-slate-900 rounded-2xl border-2 border-yellow-500 transition-all p-7 flex flex-col relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-yellow-500 text-black text-xs font-bold px-3 py-1.5 rounded-bl-xl whitespace-nowrap">
                2-Person Crew
              </div>
              <div className="w-12 h-12 flex items-center justify-center bg-yellow-500/20 border border-yellow-500/40 rounded-xl mb-5">
                <i className="ri-heart-pulse-line text-yellow-400 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Gurney / Stretcher Transport</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-4 flex-1">
                For patients who must remain lying flat. Specialized stretcher van with a dedicated two-person crew — fully equipped for safe transport.
              </p>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl px-4 py-3 mb-5">
                <p className="text-xs text-yellow-400/70 mb-0.5">Base rate</p>
                <p className="text-2xl font-extrabold text-yellow-400">$275 <span className="text-sm font-normal text-yellow-400/70">base rate</span></p>
                <p className="text-xs text-yellow-500 font-semibold mt-0.5">+ $3.50 / mile &nbsp;·&nbsp; Two-person crew included</p>
              </div>
              <button
                onClick={() => handleNavigateToHome('private-pay-form')}
                className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-sm rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                Request Stretcher Transport
              </button>
            </div>

            {/* Card 3 — Facility Partnership */}
            <div className="bg-white rounded-2xl border-2 border-slate-200 hover:border-yellow-400 transition-all p-7 flex flex-col">
              <div className="w-12 h-12 flex items-center justify-center bg-yellow-50 border border-yellow-200 rounded-xl mb-5">
                <i className="ri-building-line text-yellow-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">Facility Partnership</h3>
              <p className="text-slate-500 text-sm leading-relaxed mb-4 flex-1">
                Assisted living communities, rehab centers, and dialysis clinics. Volume-based pricing and priority scheduling for your residents.
              </p>
              <div className="bg-slate-50 rounded-xl px-4 py-3 mb-5">
                <p className="text-xs text-slate-500 mb-0.5">Pricing model</p>
                <p className="text-xl font-extrabold text-slate-900">On Request</p>
                <p className="text-xs text-yellow-600 font-semibold mt-0.5">Volume-based &amp; contract rates</p>
              </div>
              <button
                onClick={() => handleNavigateToHome('facility-form')}
                className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-sm rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                Facility Transportation Survey
              </button>
            </div>

          </div>

          <p className="text-center text-slate-400 text-xs max-w-xl mx-auto">
            Founding partners and early access members receive priority scheduling and preferred launch pricing. No commitment required to join.
          </p>
        </div>
      </div>

      {/* Schedule a Consultation Section */}
      <div className="bg-gradient-to-br from-slate-900 via-black to-slate-900 py-14 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 bg-yellow-500/20 border border-yellow-500/40 rounded-full px-4 py-2 mb-6">
              <i className="ri-calendar-check-line text-yellow-400 text-sm"></i>
              <span className="text-yellow-300 text-sm font-medium whitespace-nowrap">Book Your Consultation</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
              Schedule a Facility Introduction
            </h2>
            
            <p className="text-base sm:text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
              Meet with our team to discuss your facility's transportation needs, learn about our services, and explore how we can support your residents and patients.
            </p>
          </div>

          <div className="bg-slate-800/60 rounded-2xl p-4 sm:p-8 border border-yellow-500/20">
            <BookingCalendar
              timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/42dddeaa-cf73-4b0a-970a-40b0949d5822.3d7f20fc4445601fab9af025d13f4e0956a0dbb5e9f83a65479bf44990efb243"
              appointmentsApi="https://readdy.ai/api/public/calendar/appointments/42dddeaa-cf73-4b0a-970a-40b0949d5822.3d7f20fc4445601fab9af025d13f4e0956a0dbb5e9f83a65479bf44990efb243"
              texts={{
                heading: 'Book Your Consultation',
                selectDate: 'Select a Date',
                selectTime: 'Select a Time',
                reviewBooking: 'Review & Confirm',
                customerName: 'Your Name',
                customerPhone: 'Phone Number',
                notes: 'Additional Notes (Optional)',
                submit: 'Confirm Booking',
                modifyTime: 'Modify Time',
                back: 'Back',
                next: 'Next',
                noSlotsAvailable: 'No time slots available for this date',
                bookingSuccess: 'Consultation booked successfully! We look forward to meeting with you.',
                bookingError: 'Unable to complete booking. Please try again or contact us directly.',
                selectedDate: 'Selected Date',
                selectedTime: 'Selected Time',
                monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
                dayNames: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
              }}
              containerStyle=""
            />
          </div>
        </div>
      </div>

      {/* My Bookings Section */}
      <MyBookings />

      <Footer />
    </div>
  );
}